import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const RecorderContainer = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 0.75rem;
  padding: 1.5rem;
  background: #f9fafb;
  transition: all 0.3s ease;

  &.recording {
    border-color: #ef4444;
    background: #fef2f2;
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% {
      border-color: #ef4444;
    }
    50% {
      border-color: #dc2626;
    }
  }
`;

const RecorderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const RecorderIcon = styled.div`
  font-size: 1.5rem;
`;

const RecorderTitle = styled.div`
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
`;

const RecorderHelp = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const ControlsContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;

const RecordButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  
  &.start {
    background-color: #ef4444;
    color: white;
    
    &:hover:not(:disabled) {
      background-color: #dc2626;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
    }
  }
  
  &.stop {
    background-color: #6b7280;
    color: white;
    
    &:hover:not(:disabled) {
      background-color: #4b5563;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TimerDisplay = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #ef4444;
  font-family: monospace;
  padding: 0.5rem 1rem;
  background: white;
  border-radius: 0.5rem;
  border: 2px solid #ef4444;
`;

const AudioPlayerContainer = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
`;

const PlayerControls = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;

const PlayButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.5rem;
  background-color: #3b82f6;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  
  &:hover:not(:disabled) {
    background-color: #2563eb;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.5rem;
  background-color: #ef4444;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  
  &:hover:not(:disabled) {
    background-color: #dc2626;
  }
`;

const AudioInfo = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  color: #991b1b;
  font-size: 0.875rem;
  margin-top: 1rem;
`;

const AudioRecorder = ({ onAudioRecorded, existingAudio }) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState(existingAudio || null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioPlayerRef = useRef(null);

  useEffect(() => {
    if (existingAudio) {
      const url = URL.createObjectURL(existingAudio);
      setAudioUrl(url);
      setAudioBlob(existingAudio);
    }

    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioBlob(audioBlob);
        setAudioUrl(audioUrl);
        onAudioRecorded(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError(t('audioRecorder.errors.microphoneAccess', { defaultValue: 'Unable to access microphone. Please check your browser permissions.' }));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const playAudio = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
    }
  };

  const deleteAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    onAudioRecorded(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div>
      <RecorderContainer className={isRecording ? 'recording' : ''}>
        <RecorderHeader>
          <RecorderIcon>🎤</RecorderIcon>
          <RecorderTitle>{t('audioRecorder.title', { defaultValue: 'Voice Recording' })}</RecorderTitle>
        </RecorderHeader>

        <RecorderHelp>
          {isRecording 
            ? t('audioRecorder.help.recording', { defaultValue: 'Recording in progress... Click "Stop Recording" when finished.' })
            : audioBlob 
            ? t('audioRecorder.help.recorded', { defaultValue: 'Audio recorded successfully! You can play, delete, or record a new one.' })
            : t('audioRecorder.help.start', { defaultValue: 'Click "Start Recording" to record your pain description using your voice.' })}
        </RecorderHelp>

        <ControlsContainer>
          {!isRecording && !audioBlob && (
            <RecordButton
              type="button"
              className="start"
              onClick={startRecording}
            >
              <span>⏺</span>
              {t('audioRecorder.buttons.start', { defaultValue: 'Start Recording' })}
            </RecordButton>
          )}
          
          {isRecording && (
            <>
              <RecordButton
                type="button"
                className="stop"
                onClick={stopRecording}
              >
                <span>⏹</span>
                {t('audioRecorder.buttons.stop', { defaultValue: 'Stop Recording' })}
              </RecordButton>
              <TimerDisplay>{formatTime(recordingTime)}</TimerDisplay>
            </>
          )}
        </ControlsContainer>

        {error && (
          <ErrorMessage>
            ⚠️ {error}
          </ErrorMessage>
        )}
      </RecorderContainer>

      {audioBlob && audioUrl && (
        <AudioPlayerContainer>
          <PlayerControls>
            {!isPlaying ? (
              <PlayButton onClick={playAudio}>
                <span>▶️</span>
                {t('audioRecorder.buttons.play', { defaultValue: 'Play Recording' })}
              </PlayButton>
            ) : (
              <PlayButton onClick={pauseAudio}>
                <span>⏸</span>
                {t('audioRecorder.buttons.pause', { defaultValue: 'Pause' })}
              </PlayButton>
            )}
            
            <DeleteButton onClick={deleteAudio}>
              <span>🗑️</span>
              {t('audioRecorder.buttons.delete', { defaultValue: 'Delete' })}
            </DeleteButton>

            {!isRecording && (
              <RecordButton
                type="button"
                className="start"
                onClick={startRecording}
                style={{ marginLeft: 'auto' }}
              >
                <span>🔄</span>
                {t('audioRecorder.buttons.rerecord', { defaultValue: 'Re-record' })}
              </RecordButton>
            )}
          </PlayerControls>
          
          <AudioInfo>
            📊 {t('audioRecorder.info.duration', { defaultValue: 'Duration' })}: {formatTime(recordingTime)} • {t('audioRecorder.info.format', { defaultValue: 'Format' })}: Audio/WebM
          </AudioInfo>
          
          <audio
            ref={audioPlayerRef}
            src={audioUrl}
            onEnded={() => setIsPlaying(false)}
            style={{ display: 'none' }}
          />
        </AudioPlayerContainer>
      )}
    </div>
  );
};

export default AudioRecorder;

